import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Sistema simples de toast in-app.
 *
 *   toast.success("Mensagem")
 *   toast.error("Mensagem", { sticky: true })
 *   toast.info("Mensagem", { actionLabel: "Reativar", onAction: () => ... })
 *
 *   // Deduplica: se ja existe toast com o mesmo dedupKey, NAO cria
 *   // duplicata — retorna o id do existente. Util para evitar spam
 *   // quando uma mesma condicao dispara muitas vezes (ex.: permissao
 *   // expirada acionada por cada agendamento do tick).
 *   toast.warning("Permissao expirada", { dedupKey: "perm-expired" })
 *
 *   // Remove o(s) toast(s) com dedupKey especifico — util quando a
 *   // condicao que gerou o aviso sumiu.
 *   toast.removerPorDedupKey("perm-expired")
 *
 * Sticky: nao some sozinho.
 *
 * ARQUITETURA (revisao Bloco Parenteses 2 — fix bombardeio 429):
 *
 *   A API publica (`useToast()`) retorna referencias 100% ESTAVEIS via
 *   useRef interno — funcoes de adicionar/remover nunca mudam entre
 *   renders. Componentes que emitem toast (a maioria) NAO re-renderizam
 *   quando a lista de toasts muda.
 *
 *   Para o ToastStack que precisa RE-RENDERIZAR ao mudar a lista, use
 *   `useToastList()` (hook separado). Esse sim sofre re-render quando
 *   array muda — comportamento desejado.
 *
 *   Sem essa separacao, qualquer consumer de `useToast()` recebia uma
 *   nova `api` a cada add/remove de toast, propagando instabilidade
 *   para `useCallback`/`useEffect` dependentes — causava loops
 *   infinitos de fetch quando `AuthContext.request` dependia de `toast`.
 */

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // Ref sempre sincronizado com state — permite leitura "atual" dentro
  // de callbacks estaveis (sem precisar de `toasts` na dep do useCallback).
  const toastsRef = useRef([]);

  // Setter wrapper que atualiza ref + state juntos. Garante que callbacks
  // lendo `toastsRef.current` veem o valor mais recente sincronicamente.
  const updateToasts = useCallback((updater) => {
    setToasts((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      toastsRef.current = next;
      return next;
    });
  }, []);

  const remover = useCallback(
    (id) => {
      updateToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [updateToasts],
  );

  const removerPorDedupKey = useCallback(
    (dedupKey) => {
      if (!dedupKey) return;
      updateToasts((prev) => prev.filter((t) => t.dedupKey !== dedupKey));
    },
    [updateToasts],
  );

  const adicionar = useCallback(
    (msg, opts = {}) => {
      const dedupKey = opts.dedupKey || null;
      // Le do REF (sempre atual), nao da state direto — assim este
      // callback nao precisa ter `toasts` como dep e fica 100% estavel.
      if (dedupKey) {
        const existente = toastsRef.current.find(
          (t) => t.dedupKey === dedupKey,
        );
        if (existente) return existente.id;
      }
      const id = nextId++;
      const toast = {
        id,
        mensagem: msg,
        tone: opts.tone || "info",
        sticky: Boolean(opts.sticky),
        actionLabel: opts.actionLabel,
        onAction: opts.onAction,
        dedupKey,
      };
      updateToasts((prev) => {
        // Double-check via updater funcional (evita race com chamadas
        // simultaneas que possam ter passado pelo find inicial).
        if (dedupKey && prev.some((t) => t.dedupKey === dedupKey)) {
          return prev;
        }
        return [...prev, toast];
      });
      if (!toast.sticky) {
        const ttl = opts.ttl ?? 5000;
        setTimeout(() => remover(id), ttl);
      }
      return id;
    },
    [updateToasts, remover],
  );

  // API publica estavel — referencias NAO mudam entre renders.
  // CRITICO: NAO inclui `toasts` na dep — assim consumers de useToast()
  // nao re-renderizam quando lista de toasts muda. Isso quebra cascata
  // de instabilidade que causava bombardeio de 429 (loop infinito de
  // requests via AuthContext.request).
  const api = useMemo(
    () => ({
      success: (msg, opts = {}) => adicionar(msg, { ...opts, tone: "success" }),
      error: (msg, opts = {}) => adicionar(msg, { ...opts, tone: "danger" }),
      warning: (msg, opts = {}) => adicionar(msg, { ...opts, tone: "warning" }),
      info: (msg, opts = {}) => adicionar(msg, { ...opts, tone: "info" }),
      remover,
      removerPorDedupKey,
    }),
    [adicionar, remover, removerPorDedupKey],
  );

  // Provider value combina api estavel + toasts dinamico.
  // toasts MUDA (state) — consumers de useToastList() re-renderizam
  // quando lista muda (esperado, eh o que ToastStack precisa).
  // Consumers de useToast() nao acessam toasts (filtrado no hook abaixo).
  const value = useMemo(
    () => ({ ...api, toasts }),
    [api, toasts],
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

/**
 * Hook para emitir toasts. Retorna SO API ESTAVEL — referencias nunca
 * mudam entre renders. Componentes que so emitem (90% dos casos) usam
 * este hook e nao re-renderizam quando algum toast eh adicionado/removido.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback inerte para componentes fora do provider (ex.: testes).
    return {
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {},
      remover: () => {},
      removerPorDedupKey: () => {},
    };
  }
  // As FUNÇÕES eram estáveis, mas o objeto literal nascia novo a cada render
  // — e o value do provider inclui `toasts`, então todo consumidor re-renderiza
  // a cada toast do app inteiro. Quem usa `toast` como dep de useEffect
  // (AgendamentoContext) tinha o efeito desmontado e remontado o tempo todo.
  // useMemo sobre as funções: mesma identidade enquanto elas não mudarem.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(
    () => ({
      success: ctx.success,
      error: ctx.error,
      warning: ctx.warning,
      info: ctx.info,
      remover: ctx.remover,
      removerPorDedupKey: ctx.removerPorDedupKey,
    }),
    [
      ctx.success,
      ctx.error,
      ctx.warning,
      ctx.info,
      ctx.remover,
      ctx.removerPorDedupKey,
    ]
  );
}

/**
 * Hook para LER a lista de toasts (usado pelo ToastStack). Re-renderiza
 * quando a lista muda — comportamento desejado para a UI que mostra
 * os toasts.
 *
 * NAO use este hook se voce so quer EMITIR toasts — prefira `useToast()`
 * que retorna api estavel e nao causa re-render.
 */
export function useToastList() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return { toasts: [], remover: () => {} };
  }
  return { toasts: ctx.toasts || [], remover: ctx.remover };
}
